/**
 * ATA Code service - fetches ATA chapter/section reference data.
 * Data sourced from my_glue_db.airtechmaintenance_atacodes via DynamoDB.
 * Falls back to static embedded data when API is unavailable.
 */
import { createLogger } from '../utils/logger';
import { authedFetch } from './apiClient';
import { STATIC_ATA_CODES } from '../data/ataCodes';

const logger = createLogger('AtaCodeService');

/**
 * Represents a single ATA code record.
 * Maps to my_glue_db.airtechmaintenance_atacodes fields.
 */
export interface AtaCodeRecord {
  ata_code_id: number;
  ata_chapter: string;
  ata_sub_chapter: string;
  description: string;
}

/**
 * Entry for the Chapter dropdown.
 * Displays the full ata_chapter value.
 */
export interface AtaChapter {
  chapter: string;
  label: string;
}

/**
 * Entry for the Section dropdown.
 * Displays the full ata_sub_chapter value.
 */
export interface AtaSection {
  section: string;
  label: string;
  ataCodeId: number;
}

let cachedAtaCodes: AtaCodeRecord[] | null = null;
let cachedAtaCodesScx: AtaCodeRecord[] | null = null;
let usingLiveData = false;

/**
 * Fetches all ATA codes from the API. Caches the result since it's stagnant data.
 * Falls back to static embedded data if API is unavailable.
 */
export async function fetchAtaCodes(): Promise<AtaCodeRecord[]> {
  if (cachedAtaCodes) return cachedAtaCodes;

  try {
    const response = await authedFetch('/ata-codes');
    if (!response || !response.ok) {
      logger.warn('Failed to fetch ATA codes, using static data', { statusCode: response?.status });
      cachedAtaCodes = STATIC_ATA_CODES;
      usingLiveData = false;
      return cachedAtaCodes;
    }
    const data = await response.json();
    const codes = data.ataCodes || [];
    if (codes.length > 0) {
      cachedAtaCodes = codes;
      usingLiveData = true;
    } else {
      cachedAtaCodes = STATIC_ATA_CODES;
      usingLiveData = false;
    }
    return cachedAtaCodes as AtaCodeRecord[];
  } catch (err: unknown) {
    if (err instanceof TypeError) {
      logger.error('Network error fetching ATA codes, using static data', { error: err.message });
    } else {
      logger.error('Error fetching ATA codes, using static data', {
        error: err instanceof Error ? err.message : 'Unknown error',
      });
    }
    cachedAtaCodes = STATIC_ATA_CODES;
    usingLiveData = false;
    return cachedAtaCodes;
  }
}

/**
 * Fetches B738 aircraft-type-specific ATA codes (ata_code_id 178-607).
 * When API is unavailable, falls back to all static data (unfiltered) for usability.
 */
export async function fetchAtaCodesB738(): Promise<AtaCodeRecord[]> {
  if (cachedAtaCodesScx) return cachedAtaCodesScx;

  try {
    const response = await authedFetch('/ata-codes/scx');
    if (!response || !response.ok) {
      logger.warn('Failed to fetch B738 ATA codes, using all static data', {
        statusCode: response?.status,
      });
      cachedAtaCodesScx = STATIC_ATA_CODES;
      return cachedAtaCodesScx;
    }
    const data = await response.json();
    const codes = data.ataCodes || [];
    if (codes.length > 0) {
      cachedAtaCodesScx = codes;
    } else {
      cachedAtaCodesScx = STATIC_ATA_CODES;
    }
    return cachedAtaCodesScx as AtaCodeRecord[];
  } catch (err: unknown) {
    logger.error('Error fetching B738 ATA codes, using all static data', {
      error: err instanceof Error ? err.message : 'Unknown error',
    });
    cachedAtaCodesScx = STATIC_ATA_CODES;
    return cachedAtaCodesScx;
  }
}

let cachedAtaCodesB767: AtaCodeRecord[] | null = null;

/**
 * Fetches B767 aircraft-type-specific ATA codes (ata_code_id 608-1117).
 * Used for B762 and B763 aircraft types.
 * When API is unavailable, falls back to all static data for usability.
 */
export async function fetchAtaCodesB767(): Promise<AtaCodeRecord[]> {
  if (cachedAtaCodesB767) return cachedAtaCodesB767;

  try {
    const response = await authedFetch('/ata-codes/b767');
    if (!response || !response.ok) {
      logger.warn('Failed to fetch B767 ATA codes, using all static data', {
        statusCode: response?.status,
      });
      cachedAtaCodesB767 = STATIC_ATA_CODES;
      return cachedAtaCodesB767;
    }
    const data = await response.json();
    const codes = data.ataCodes || [];
    if (codes.length > 0) {
      cachedAtaCodesB767 = codes;
    } else {
      cachedAtaCodesB767 = STATIC_ATA_CODES;
    }
    return cachedAtaCodesB767 as AtaCodeRecord[];
  } catch (err: unknown) {
    logger.error('Error fetching B767 ATA codes, using all static data', {
      error: err instanceof Error ? err.message : 'Unknown error',
    });
    cachedAtaCodesB767 = STATIC_ATA_CODES;
    return cachedAtaCodesB767;
  }
}

/**
 * Pads a single-digit number at the start of a label with a leading zero.
 * e.g. "5 - TIME LIMITS/MX CHECKS" → "05 - TIME LIMITS/MX CHECKS"
 * e.g. "0 - General" → "00 - General"
 */
function padLabel(value: string): string {
  const trimmed = value.trim();
  const match = trimmed.match(/^(\d+)(.*)/);
  if (match && match[1].length === 1) {
    return `0${match[1]}${match[2]}`;
  }
  return trimmed;
}

/**
 * Pads the leading numeric portion of a chapter/section value to two digits.
 * e.g. "5" → "05", "21" → "21", "" → "".
 */
function padAtaSegment(value: string): string {
  const num = (value || '').match(/^\d+/)?.[0] || '';
  if (!num) return '';
  return num.length === 1 ? `0${num}` : num;
}

/**
 * Formats an ATA chapter/section pair for display as two-digit-hyphen-two-digit
 * (e.g. chapter "5" + section "1" → "05-01"). When only the chapter is present,
 * returns the zero-padded chapter alone. Returns '—' when neither is present.
 */
export function formatAtaCode(chapter: string, section: string): string {
  const chapterPart = padAtaSegment(chapter);
  if (!chapterPart) return '—';
  const sectionPart = padAtaSegment(section);
  return sectionPart ? `${chapterPart}-${sectionPart}` : chapterPart;
}

/**
 * Returns all unique ata_chapter values for the Chapter dropdown.
 */
export function getChapters(codes: AtaCodeRecord[]): AtaChapter[] {
  const chapterSet = new Set<string>();

  for (const code of codes) {
    chapterSet.add(code.ata_chapter);
  }

  return Array.from(chapterSet)
    .map((chapter) => ({
      chapter,
      label: padLabel(chapter),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
}

/**
 * Returns unique ata_sub_chapter values filtered by the selected chapter for the Section dropdown.
 */
export function getSections(codes: AtaCodeRecord[], selectedChapter?: string): AtaSection[] {
  const filtered = selectedChapter
    ? codes.filter((code) => code.ata_chapter === selectedChapter)
    : codes;

  const sectionMap = new Map<string, number>();

  for (const code of filtered) {
    if (!sectionMap.has(code.ata_sub_chapter)) {
      sectionMap.set(code.ata_sub_chapter, code.ata_code_id);
    }
  }

  return Array.from(sectionMap.entries())
    .map(([section, ataCodeId]) => ({
      section,
      label: padLabel(section),
      ataCodeId,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
}

/**
 * ATA code ID ranges by aircraft type — each type maps to a single contiguous
 * `ata_code_id` range. Derived from the authoritative source
 * docs/reference/ata-code-list.xlsx, where every ATA code row carries a
 * per-type membership flag. Each
 * type's range is self-contained, so a type's list never overlaps another's:
 * that overlap was the source of the duplicate dropdown entries.
 *
 * B762 and B763 share the same (B767-family) range. Keys match the
 * `FleetRecord.aircraftType` values returned by the fleet service.
 */
// Keyed by every aircraft-type spelling that reaches the modal: ICAO-style
// codes from the fleet/Andes plus the fuller IATA/marketing strings that carrier
// feeds and the CSB web scraper emit (e.g. 'B767-300'). Variants of one family
// share that family's ata_code_id range so the Chapter/Section dropdowns show
// exactly that type's codes regardless of how the type was recorded.
const ATA_RANGES_BY_AIRCRAFT_TYPE: Record<string, { min: number; max: number }> = {
  B738: { min: 178, max: 607 },
  '737-800': { min: 178, max: 607 },
  'B737-800': { min: 178, max: 607 },
  B762: { min: 608, max: 1117 },
  B763: { min: 608, max: 1117 },
  '767-200': { min: 608, max: 1117 },
  '767-300': { min: 608, max: 1117 },
  'B767-200': { min: 608, max: 1117 },
  'B767-300': { min: 608, max: 1117 },
  A722: { min: 1118, max: 1570 },
  A330: { min: 1571, max: 2370 },
  'A330-200': { min: 1571, max: 2370 },
  'A330-300': { min: 1571, max: 2370 },
};

/**
 * Filters ATA codes to the single list for the given aircraft type.
 *
 * Returns only the codes within that type's `ata_code_id` range, so the
 * Chapter/Section dropdowns show exactly one aircraft type's ATA codes (no
 * cross-type duplicates). When using static fallback data (API unavailable) the
 * codes are returned unfiltered, and an unknown/empty aircraft type also returns
 * all codes so an existing record's saved classification still resolves.
 */
export function filterAtaCodesByAircraftType(
  codes: AtaCodeRecord[],
  aircraftType: string,
): AtaCodeRecord[] {
  // Only apply filtering when using live data from the API (which has the full dataset).
  if (!usingLiveData) return codes;
  const range = ATA_RANGES_BY_AIRCRAFT_TYPE[(aircraftType || '').toUpperCase()];
  if (!range) return codes;
  return codes.filter((code) => code.ata_code_id >= range.min && code.ata_code_id <= range.max);
}
